import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, Injector, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ModalOverlayService {
  private defaultOverlayConfig = {
    hasBackdrop: true,
    panelClass: ['generic-modal__panel'],
    backdropClass: 'generic-modal__backdrop',
    scrollStrategy: this.overlay.scrollStrategies.noop(),
  };

  private overlayRef: OverlayRef[] = [];
  private containerRef$: BehaviorSubject<{
    viewContainerRef: ViewContainerRef;
    injector: Injector;
  } | null> = new BehaviorSubject(null);
  private modalResult$: Subject<any> = new Subject();

  constructor(private overlay: Overlay) {}

  public registerCurrentContainerRef(viewContainerRef: ViewContainerRef, injector: Injector) {
    this.containerRef$.next({
      viewContainerRef,
      injector,
    });
  }

  public unregisterCurrentContainerRef() {
    this.containerRef$.next(null);
  }

  public openModal<T>(
    component: ComponentType<T>,
    overrideConfig: OverlayConfig = {},
    dataProperty?: string,
    data?: any,
    isClosePreviewsModal = true
  ) {
    return this.containerRef$.pipe(
      take(1),
      switchMap((containerRef) => {
        // If no containerRef is registered, overlay will attach to the root overlay container
        if (isClosePreviewsModal) {
          this.closeModal(null);
        }

        const configs = new OverlayConfig({
          ...this.defaultOverlayConfig,
          ...overrideConfig,
        });

        this.overlayRef.push(this.overlay.create(configs));
        try {
          const portal = new ComponentPortal(component, containerRef?.viewContainerRef, containerRef?.injector);
          const overlayRef = this.overlayRef[this.overlayRef.length - 1];
          const ref = overlayRef.attach(portal);
          // Close on backdrop click & ESC for consistent UX (component may also call close)
          overlayRef
            .backdropClick()
            .pipe(take(1))
            .subscribe(() => this.closeModal(null));
          overlayRef
            .keydownEvents()
            .pipe(take(1))
            .subscribe((ev) => {
              if (ev.key === 'Escape' || ev.key === 'Esc') {
                this.closeModal(null);
              }
            });
          const instance = ref.instance as any;
          if (dataProperty && data) {
            instance[dataProperty] = data;
          }
          return this.modalResult$.pipe(take(1));
        } catch (err) {
          // Cleanup the overlay we just created
          this.overlayRef.pop()?.dispose();
          // Emit a null result so callers can continue their streams safely
          const result$ = new BehaviorSubject<any>(null);
          const once$ = result$.pipe(take(1));
          result$.next(null);
          result$.complete();
          return once$;
        }
      })
    );
  }

  public closeModal(modalResult: any) {
    // No reliance on global event; callers can handle DOM events explicitly
    this.modalResult$.next(modalResult);

    if (this.overlayRef.length) {
      this.overlayRef.pop().dispose();
    }
  }
}
